import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  LinearProgress,
} from '@mui/material';
import { ExpandMore, Apartment } from '@mui/icons-material';
import { useProjectFloors } from '../../hooks/useApiCache';
import { FloorSummary, FloorUnit, UnitStatus } from '../../types';

interface FloorPlanViewProps {
  projectId: string;
}

const unitStatusColor: Record<UnitStatus, string> = {
  [UnitStatus.AVAILABLE]: '#4caf50',
  [UnitStatus.RESERVED]: '#ff9800',
  [UnitStatus.SOLD]: '#f44336',
  [UnitStatus.RENTED]: '#2196f3',
};

const unitStatusBgColor: Record<UnitStatus, string> = {
  [UnitStatus.AVAILABLE]: '#e8f5e9',
  [UnitStatus.RESERVED]: '#fff3e0',
  [UnitStatus.SOLD]: '#ffebee',
  [UnitStatus.RENTED]: '#e3f2fd',
};

const unitStatusLabel: Record<UnitStatus, string> = {
  [UnitStatus.AVAILABLE]: 'Available',
  [UnitStatus.RESERVED]: 'Reserved',
  [UnitStatus.SOLD]: 'Sold',
  [UnitStatus.RENTED]: 'Rented',
};

const UnitCard: React.FC<{ unit: FloorUnit }> = ({ unit }) => {
  const status = (unit.unitStatus || UnitStatus.AVAILABLE) as UnitStatus;
  const color = unitStatusColor[status] ?? '#9e9e9e';
  const bg = unitStatusBgColor[status] ?? '#f5f5f5';

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={700}>
            {unit.title}
          </Typography>
          {unit.unitNumber && (
            <Typography variant="caption" display="block">
              Unit #{unit.unitNumber}
            </Typography>
          )}
          <Typography variant="caption" display="block">
            {unit.propertyType}
          </Typography>
          {unit.price != null && (
            <Typography variant="caption" display="block">
              ${unit.price.toLocaleString()}
            </Typography>
          )}
          <Typography variant="caption" display="block" sx={{ color }}>
            {unitStatusLabel[status]}
          </Typography>
        </Box>
      }
      arrow
    >
      <Paper
        elevation={0}
        sx={{
          width: 64,
          height: 64,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: bg,
          border: `2px solid ${color}`,
          borderRadius: 1,
          cursor: 'default',
          transition: 'transform 0.15s ease',
          '&:hover': { transform: 'scale(1.08)' },
        }}
      >
        <Apartment sx={{ fontSize: 20, color }} />
        {unit.unitNumber && (
          <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: '0.6rem', lineHeight: 1 }}>
            {unit.unitNumber}
          </Typography>
        )}
      </Paper>
    </Tooltip>
  );
};

const FloorRow: React.FC<{ floorData: FloorSummary }> = ({ floorData }) => {
  const total =
    floorData.available + floorData.reserved + floorData.sold + floorData.rented;

  return (
    <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: '8px !important', overflow: 'hidden' }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
          <Box
            sx={{
              minWidth: 60,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              fontWeight: 700,
              fontSize: '0.85rem',
            }}
          >
            {floorData.floor === 0 ? 'G' : `F${floorData.floor}`}
          </Box>

          {/* Status chips */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flex: 1 }}>
            {floorData.available > 0 && (
              <Chip
                label={`${floorData.available} Available`}
                size="small"
                sx={{ bgcolor: '#e8f5e9', color: '#388e3c', fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
            {floorData.reserved > 0 && (
              <Chip
                label={`${floorData.reserved} Reserved`}
                size="small"
                sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
            {floorData.sold > 0 && (
              <Chip
                label={`${floorData.sold} Sold`}
                size="small"
                sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
            {floorData.rented > 0 && (
              <Chip
                label={`${floorData.rented} Rented`}
                size="small"
                sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
            {total === 0 && (
              <Typography variant="caption" color="text.secondary">
                No units
              </Typography>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {total} unit{total !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ bgcolor: 'grey.50' }}>
        {floorData.units.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
            No units on this floor yet
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, py: 1 }}>
            {floorData.units.map((unit) => (
              <UnitCard key={unit.id} unit={unit} />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const FloorPlanView: React.FC<FloorPlanViewProps> = ({ projectId }) => {
  const { data: floors, isLoading, error } = useProjectFloors(projectId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load floor summary. Please try again.
      </Alert>
    );
  }

  if (!floors || floors.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Apartment sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No floor data available yet.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Link properties to this project to see the floor plan.
        </Typography>
      </Box>
    );
  }

  // Aggregate totals
  const totals = floors.reduce(
    (acc: { available: number; reserved: number; sold: number; rented: number }, f: FloorSummary) => ({
      available: acc.available + f.available,
      reserved: acc.reserved + f.reserved,
      sold: acc.sold + f.sold,
      rented: acc.rented + f.rented,
    }),
    { available: 0, reserved: 0, sold: 0, rented: 0 }
  );

  const totalUnits = totals.available + totals.reserved + totals.sold + totals.rented;
  const soldOrRentedPct = totalUnits > 0 ? Math.round(((totals.sold + totals.rented) / totalUnits) * 100) : 0;

  return (
    <Box>
      {/* Summary bar */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[
          { label: 'Available', count: totals.available, color: '#4caf50', bg: '#e8f5e9' },
          { label: 'Reserved', count: totals.reserved, color: '#ff9800', bg: '#fff3e0' },
          { label: 'Sold', count: totals.sold, color: '#f44336', bg: '#ffebee' },
          { label: 'Rented', count: totals.rented, color: '#2196f3', bg: '#e3f2fd' },
        ].map(({ label, count, color, bg }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                textAlign: 'center',
                bgcolor: bg,
                border: `1px solid ${color}30`,
                borderRadius: 2,
              }}
            >
              <Typography variant="h5" fontWeight={700} sx={{ color }}>
                {count}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Overall occupancy bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Occupancy (Sold + Rented)
          </Typography>
          <Typography variant="caption" fontWeight={700}>
            {soldOrRentedPct}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={soldOrRentedPct}
          sx={{ height: 8, borderRadius: 1, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' } }}
        />
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {Object.entries(unitStatusLabel).map(([status, label]) => (
          <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: unitStatusColor[status as UnitStatus],
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Floors (highest first) */}
      <Box>
        {[...floors].reverse().map((floorData: FloorSummary) => (
          <FloorRow key={floorData.floor} floorData={floorData} />
        ))}
      </Box>
    </Box>
  );
};

export default FloorPlanView;
